import React, { useState, useCallback, ReactNode } from 'react'
import clsx from 'clsx'
import MenuIcon from '@mui/icons-material/Menu'
import CloseIcon from '@mui/icons-material/Close'
import Drawer from '@mui/material/Drawer'
import { Link } from 'react-router-dom'

import Button, { ButtonProps } from 'components/Button'
import ButtonBase from 'components/Button/ButtonBase'
import ButtonConnectMetamask from 'components/Button/ButtonConnectMetamask'
import ImgPure from 'components/Img'
import { route } from 'routes'
import { defaultPageTitle, values } from 'appConstants'
import { NavbarChildProps, NavbarProps } from '.'
import DotIcon from 'components/Icon/DotIcon'
import Typography from 'components/Typography'
import CryptoUtils from 'utils/CryptoUtils'

import ImgLogo from 'assets/images/logo-artmine-playform@3x.png'

import stylesGlobal from 'stylesGlobal.module.scss'
import styles from './Navbar.module.scss'

type MenuItemProps = Pick<ButtonProps, 'href' | 'onClick' | 'target' | 'variant' | 'disabled'> & {
  className?: string
  label: ReactNode
  to?: any
}

const MenuItem: React.FC<MenuItemProps> = props => {
  const { className, href, label, disabled, onClick, target, to, variant } = props

  return (
    <div className={clsx(stylesGlobal.MarginBottom12, styles.MenuItemMobile, className)}>
      {variant ? (
        <Button
          disabled={disabled}
          to={to}
          className={clsx(stylesGlobal.FontSemibold)}
          href={href}
          onClick={onClick}
          target={target}
          variant={variant}
          fullWidth
        >
          {label}
        </Button>
      ) : (
        <ButtonBase
          disabled={disabled}
          href={href}
          className={clsx(
            stylesGlobal.WidthFull,
            stylesGlobal.JustifyContentFlexStart,
            styles.MenuItemMobileButtonBase
          )}
          onClick={onClick}
          target={target}
          to={to}
        >
          {label}
        </ButtonBase>
      )}
    </div>
  )
}

const NavbarMobile: React.FC<NavbarChildProps & Pick<NavbarProps, 'disconnect'>> = props => {
  const [open, setOpen] = useState(false)
  const { loading, userAddressShort, disconnect, onClickConnectMetamask } = props

  const handleCloseDrawer = useCallback(() => {
    setOpen(false)
  }, [])

  return (
    <div className={clsx(styles.NavbarMobileWrapper)}>
      <div
        className={clsx(
          stylesGlobal.PositionFixed,
          stylesGlobal.Top0,
          stylesGlobal.Left0,
          stylesGlobal.WidthFull,
          stylesGlobal.DisplayFlex,
          stylesGlobal.JustifyContentSpaceBetween,
          stylesGlobal.PaddingX12,
          stylesGlobal.BorderBottomPrimary,
          styles.NavbarMobile
        )}
      >
        <Link
          to={route.ROOT.getUrl()}
          className={clsx(
            stylesGlobal.DisplayFlex,
            stylesGlobal.AlignItemsCenter,
            stylesGlobal.HeightFull,
            styles.Logo
          )}
          onClick={handleCloseDrawer}
        >
          <ImgPure
            src={ImgLogo}
            className={clsx(
              stylesGlobal.DisplayBlock,
              stylesGlobal.PointerEventsNone,
              stylesGlobal.UserSelectNone
            )}
            title={defaultPageTitle}
            alt={defaultPageTitle}
            fullWidth
          />
        </Link>

        <div
          className={clsx(
            stylesGlobal.DisplayFlex,
            stylesGlobal.AlignItemsCenter,
            stylesGlobal.HeightFull
          )}
        >
          <ButtonBase
            onClick={() => {
              setOpen(prev => !prev)
            }}
          >
            {open ? <CloseIcon /> : <MenuIcon />}
          </ButtonBase>
        </div>
      </div>

      <Drawer
        anchor="right"
        open={open}
        onClose={handleCloseDrawer}
        className={styles.DrawerMobile}
        classes={{
          paper: clsx(stylesGlobal.WidthFull, styles.DrawerMobilePaper)
        }}
      >
        <div className={styles.MenuMobile}>
          <MenuItem
            label="Explore"
            to={route.MINE_PROJECTS.getUrl({}, { ...values.BASE_PROJECT_LIST_PARAM })}
            onClick={handleCloseDrawer}
          />
          <MenuItem label="About" to={route.ABOUT.getUrl()} onClick={handleCloseDrawer} />
          <MenuItem
            label="My Collection"
            to={route.COLLECTIONS.getUrl()}
            onClick={handleCloseDrawer}
          />

          <MenuItem
            label="Create"
            href={values.PLAYFORM_CREATE_URL}
            target="_blank"
            onClick={handleCloseDrawer}
          />

          <MenuItem label="Contact Us" to={route.CONTACT.getUrl()} onClick={handleCloseDrawer} />

          <div
            className={clsx(
              stylesGlobal.MarginTop8,
              stylesGlobal.MarginBottom24,
              stylesGlobal.BorderTopPrimary
            )}
          />

          {userAddressShort ? (
            <div>
              <div className={stylesGlobal.MarginBottom24}>
                <Typography variant="body1" color={'light'}>
                  Connected to:
                </Typography>

                <Typography variant="subtitle1" className={stylesGlobal.MarginTop4}>
                  <DotIcon color="green" />
                  {userAddressShort}
                </Typography>
              </div>

              <ButtonConnectMetamask
                loading={loading}
                onClick={() => {
                  disconnect()
                }}
                userAddressShort={userAddressShort}
                hideUserAddress
                fullWidth
              />

              {/* <div>
                <Button
                  className={stylesGlobal.MarginTop24}
                  variant="outlined"
                  color="error"
                  fullWidth
                  onClick={() => {
                    disconnect()
                  }}
                >
                  Disconnect Wallet
                </Button>
              </div> */}
            </div>
          ) : null}

          {/* {!userAddressShort ? (
            <MenuItem
              disabled={loading}
              label={loading ? 'Connecting...' : 'Connect to Metamask'}
              variant="outlined"
              onClick={onClickConnectMetamask}
            />
          ) : null} */}

          {!userAddressShort ? (
            <div>
              <div className={clsx(stylesGlobal.MarginBottom12, styles.MenuItemMobile)}>
                <ButtonConnectMetamask
                  loading={loading}
                  onClick={onClickConnectMetamask}
                  fullWidth
                />
              </div>

              <div className={stylesGlobal.MarginTop24}>
                <Typography variant="subtitle1" fontWeight="bold">
                  No wallet detected
                </Typography>

                <Typography variant="body2" color="light" className={stylesGlobal.MarginTop4}>
                  {CryptoUtils.errorMessage()}
                </Typography>
              </div>
            </div>
          ) : null}
        </div>
      </Drawer>
    </div>
  )
}

export default NavbarMobile
